/**
 * Rules for a graphic layered over a scene's other content — footage, a speaker, a stock design —
 * rather than owning its frame. They run only when the context says so, and carry no `guidance`:
 * the composition guide is written for graphics that own the frame, and the layering contract is
 * stated once, in the skill for layered graphics.
 */
import {
    parseColor,
    seeThroughShareUnderGlyph,
    sitsOnSceneBeneath,
    type ImageLike,
} from './contrast';
import { shouldAssessContrast } from './contrastRule';
import { describeElement, visibleThroughWindows } from './measure';
import type { FrameSnapshot } from './measure';
import type { RuntimeLintContext, RuntimeLintRule } from './runtimeRules';

/** How much of the frame a layered graphic may cover with solid paint before it hides the scene. */
export const MAX_PAINTED_SHARE = 0.4;

/** Paint at least this opaque hides what is beneath it; anything fainter still lets it read. */
export const COVERING_ALPHA = 200;

async function imageOf(frame: FrameSnapshot): Promise<ImageLike | null> {
    try {
        const canvas = await frame.rasterize();
        const context2d = canvas.getContext('2d');
        if (!context2d) return null;
        const { data } = context2d.getImageData(0, 0, canvas.width, canvas.height);
        return { data, width: canvas.width, height: canvas.height };
    } catch {
        return null; // a frame that won't rasterize says nothing about what it covers
    }
}

export function paintedShare({ data }: ImageLike): number {
    let painted = 0;
    for (let index = 3; index < data.length; index += 4) {
        if (data[index] >= COVERING_ALPHA) painted++;
    }
    return painted / (data.length / 4);
}

export const overlayHidesScene: RuntimeLintRule = {
    id: 'overlay-hides-scene',
    severity: 'error',
    async check(context: RuntimeLintContext): Promise<string[]> {
        if (!context.overlaysScene) return [];
        let worst: { share: number; progress: number } | null = null;
        for (const frame of context.frames ?? []) {
            const image = await imageOf(frame);
            if (!image) continue;
            const share = paintedShare(image);
            if (share > MAX_PAINTED_SHARE && (!worst || share > worst.share)) {
                worst = { share, progress: frame.progress };
            }
        }
        if (!worst) return [];
        return [
            `At ${Math.round(worst.progress * 100)}% of the scene this graphic covers ` +
                `${Math.round(worst.share * 100)}% of the frame with solid paint, hiding the scene ` +
                `it is layered over (at most ${MAX_PAINTED_SHARE * 100}% may be covered). Leave \`html\`, ` +
                '`body` and any full-frame container transparent, and keep the graphic to a band ' +
                'or a side of the frame.',
        ];
    },
};

export const textWithoutPlate: RuntimeLintRule = {
    id: 'text-without-plate',
    severity: 'error',
    async check(context: RuntimeLintContext): Promise<string[]> {
        if (!context.overlaysScene) return [];
        const offenders = new Set<Element>();
        for (const frame of context.frames ?? []) {
            const texts = frame.elements.filter(shouldAssessContrast);
            if (texts.length === 0) continue;
            const image = await imageOf(frame);
            if (!image) continue;
            for (const measurement of texts) {
                const foreground = parseColor(measurement.color);
                if (!foreground) continue;
                // Only what shows through any clipping window is drawn — a digit wheel's strip runs
                // far past the one digit it shows.
                const shown = visibleThroughWindows(
                    measurement.inkRect,
                    measurement.clipRect,
                    context.canvasSize,
                );
                const share = seeThroughShareUnderGlyph(image, shown, foreground);
                if (sitsOnSceneBeneath(share)) {
                    offenders.add(measurement.element);
                }
            }
        }
        return [...offenders].map(
            (element) =>
                `${describeElement(element)} is drawn straight onto the scene beneath, which ` +
                'this check cannot see, so its legibility cannot be known. Set it on ' +
                'a plate — a solid or near-solid panel behind the copy.',
        );
    },
};

export const CORE_OVERLAY_LINT_RULES: RuntimeLintRule[] = [overlayHidesScene, textWithoutPlate];
